import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhotoGallery } from '@/components/PhotoGallery';
import { getCriteriaForType } from '@/utils/criteria';
import { ArrowLeft, Send, MapPin, DollarSign, Layers, Home, Target, Trash2, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { io } from 'socket.io-client';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProposalDetailPage = () => {
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showGallery, setShowGallery] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);

  useEffect(() => {
    checkAuth();
    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, []);

  useEffect(() => {
    if (conversation) {
      fetchMessages();
      if (socketRef.current) {
        socketRef.current.emit('join_conversation', { conversation_id: conversation.conversation_id });
      }
    }
  }, [conversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      await fetchProposalDetails();
    } catch (error) {
      toast.error('Connexion requise');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchProposalDetails = async () => {
    try {
      const receivedRes = await axios.get(`${API}/proposals/received`, { withCredentials: true });
      let prop = receivedRes.data.find(p => p.proposal_id === proposalId);
      
      if (!prop) {
        const sentRes = await axios.get(`${API}/proposals/sent`, { withCredentials: true });
        prop = sentRes.data.find(p => p.proposal_id === proposalId);
      }
      
      if (prop) {
        setProposal(prop);
        await findConversation(prop.proposal_id);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const findConversation = async (propId) => {
    try {
      const response = await axios.get(`${API}/conversations`, { withCredentials: true });
      const conv = response.data.find(c => c.proposal_id === propId);
      if (conv) {
        setConversation(conv);
        initSocket();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const initSocket = () => {
    socketRef.current = io(BACKEND_URL, { transports: ['websocket'], withCredentials: true });
    socketRef.current.on('new_message', (data) => {
      if (conversation && data.conversation_id === conversation.conversation_id) {
        setMessages(prev => [...prev, data.message]);
      }
    });
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/conversations/${conversation.conversation_id}/messages`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !conversation) return;
    try {
      await axios.post(`${API}/messages`, {
        conversation_id: conversation.conversation_id,
        content: newMessage
      }, { withCredentials: true });
      setNewMessage('');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading || !proposal) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header
        currentUser={user}
        acquirerAdsCount={0}
        onLoginClick={() => {}}
        onAboutClick={() => {}}
        onCreateAdClick={() => {}}
        onLogout={async () => {
          await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
          navigate('/');
          window.location.reload();
        }}
      />

      <div className="max-w-6xl mx-auto px-6 py-8">
        <Button onClick={() => navigate('/mon-compte')} variant="ghost" className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>

        {proposal.status === 'deleted' && (
          <div className="bg-red-50 border-2 border-red-400 text-red-800 px-6 py-4 rounded-lg mb-6">
            Cette proposition a été retirée par le vendeur. La conversation est fermée.
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-sm border border-border p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-bold text-foreground">Détails du bien</h2>
              {user?.user_id === proposal.seller_user_id && proposal.status !== 'deleted' && (
                <Button
                  onClick={async () => {
                    if (confirm('Retirer cette proposition ?')) {
                      try {
                        await axios.delete(`${API}/proposals/${proposalId}`, { withCredentials: true });
                        toast.success('Proposition retirée');
                        window.location.reload();
                      } catch (error) {
                        toast.error('Erreur');
                      }
                    }
                  }}
                  variant="destructive"
                  size="sm"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Retirer
                </Button>
              )}
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 className="font-heading text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                  <Home className="w-5 h-5" />
                  {proposal.property_type}
                </h3>
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {proposal.property_address}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted/50 p-4 rounded">
                  <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    <DollarSign className="w-3 h-3" />
                    Prix
                  </p>
                  <p className="text-xl font-bold text-foreground">{proposal.price.toLocaleString('fr-FR')} €</p>
                </div>
                <div className="bg-muted/50 p-4 rounded">
                  <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    <Layers className="w-3 h-3" />
                    Surface
                  </p>
                  <p className="text-lg font-bold text-foreground">{proposal.surface} m²</p>
                </div>
                {proposal.rooms && (
                  <div className="bg-muted/50 p-4 rounded">
                    <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                      <Layers className="w-3 h-3" />
                      Pièces
                    </p>
                    <p className="text-lg font-bold text-foreground">{proposal.rooms}</p>
                  </div>
                )}
                {proposal.match_score && (
                  <div className={`p-4 rounded ${
                    proposal.match_score >= 80 ? 'bg-green-100' :
                    proposal.match_score >= 60 ? 'bg-yellow-100' : 'bg-orange-100'
                  }`}>
                    <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                      <Target className="w-3 h-3" />
                      Score
                    </p>
                    <p className="text-lg font-bold">{proposal.match_score}%</p>
                  </div>
                )}
              </div>

              {proposal.photos && proposal.photos.length > 0 ? (
                <div>
                  <p className="font-heading text-sm font-semibold text-foreground mb-3">Photos ({proposal.photos.length})</p>
                  <div className="grid grid-cols-2 gap-2">
                    {proposal.photos.map((photo, i) => {
                      let photoUrl;
                      if (photo.startsWith('http')) {
                        photoUrl = photo;
                      } else if (photo.startsWith('/api/')) {
                        photoUrl = `${BACKEND_URL}${photo}`;
                      } else {
                        photoUrl = `${BACKEND_URL}/api${photo}`;
                      }
                      console.log('Loading photo:', photoUrl);
                      return (
                        <div 
                          key={i} 
                          className="relative cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => {
                            setGalleryIndex(i);
                            setShowGallery(true);
                          }}
                        >
                          <img
                            src={photoUrl}
                            alt={`Photo ${i+1}`}
                            className="w-full h-32 object-cover rounded-lg border-2 border-blue-200"
                            onLoad={() => console.log('Photo loaded:', photoUrl)}
                            onError={(e) => {
                              console.error('Photo failed:', photoUrl);
                              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect width="200" height="200" fill="%23ddd"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999">Photo</text></svg>';
                            }}
                          />
                          <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
                            {i + 1}/{proposal.photos.length}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-100 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Aucune photo</p>
                </div>
              )}

              {proposal.personalized_message && (
                <div className="bg-muted/30 border border-border p-4 rounded">
                  <p className="font-heading text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" />
                    Message
                  </p>
                  <p className="font-body text-foreground">{proposal.personalized_message}</p>
                </div>
              )}

              {proposal.criteria && Object.keys(proposal.criteria).length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3">🎯 Caractéristiques du bien</p>
                  <div className="space-y-2">
                    {Object.entries(proposal.criteria).map(([categoryKey, values]) => {
                      if (!values || values.length === 0) return null;
                      const criteriaCategories = getCriteriaForType(proposal.property_type);
                      const category = criteriaCategories[categoryKey];
                      if (!category) return null;
                      
                      return (
                        <div key={categoryKey} className="bg-purple-50 p-3 rounded-lg">
                          <p className="text-xs text-gray-600 mb-1">{category.label}</p>
                          <div className="flex flex-wrap gap-1">
                            {values.map((val, i) => {
                              const option = category.options.find(opt => opt.value === val);
                              return option ? (
                                <span key={i} className="bg-white px-2 py-0.5 rounded-full text-xs font-semibold text-gray-800">
                                  ✓ {option.label}
                                </span>
                              ) : null;
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-border overflow-hidden" style={{ height: '600px' }}>
            <div className="flex flex-col h-full">
              <div className="p-4 border-b border-border bg-black text-white">
                <h3 className="font-heading font-bold flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Conversation
                </h3>
                <p className="text-sm opacity-90">{conversation?.other_user?.name || 'Pas encore de conversation'}</p>
              </div>

              {!conversation ? (
                <div className="flex-1 flex items-center justify-center text-gray-600 text-sm p-4 text-center">
                  <p>La conversation démarrera quand vous enverrez le premier message</p>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {messages.map((msg) => (
                      <div key={msg.message_id} className={`flex ${msg.sender_id === user.user_id ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[70%] rounded-2xl p-3 text-sm ${
                          msg.sender_id === user.user_id
                            ? 'bg-blue-600 text-white rounded-br-sm'
                            : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                        }`}>
                          <p>{msg.content}</p>
                          <p className={`text-xs mt-1 ${msg.sender_id === user.user_id ? 'text-blue-100' : 'text-gray-500'}`}>
                            {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="p-4 border-t border-border bg-white">
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && proposal.status !== 'deleted' && sendMessage()}
                        placeholder={proposal.status === 'deleted' ? 'Conversation fermée' : 'Votre message...'}
                        className="rounded border"
                        disabled={proposal.status === 'deleted'}
                      />
                      <Button 
                        onClick={sendMessage} 
                        size="icon" 
                        className="bg-black hover:bg-black/90 text-white"
                        disabled={proposal.status === 'deleted'}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {showGallery && proposal.photos && (
        <PhotoGallery
          photos={proposal.photos.map(photo => {
            if (photo.startsWith('http')) return photo;
            if (photo.startsWith('/api/')) return `${BACKEND_URL}${photo}`;
            return `${BACKEND_URL}/api${photo}`;
          })}
          initialIndex={galleryIndex}
          onClose={() => setShowGallery(false)}
        />
      )}
    </div>
  );
};

export default ProposalDetailPage;
